import { Color, Spacing } from '@persona-id/design-tokens';
import { ChevronRightIcon, EditIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import { useEffect, useMemo } from 'react';
import { Draggable, DraggableProvidedDragHandleProps, Droppable } from 'react-beautiful-dnd';
import styled from 'styled-components';

import {
  EditorableSchema,
  FocusReason,
  RelationSchemaType,
} from 'dashboard/components-lib/FieldSchemaEditor/constants';
import ExpandableFieldRow, {
  NEST_SPACING,
} from 'dashboard/components-lib/FieldSchemaEditor/ExpandableFieldRow';
import {
  convertDraggableId,
  convertDroppableId,
} from 'dashboard/components-lib/FieldSchemaEditor/FieldSchemaEditorTable';
import { Inserter } from 'dashboard/components-lib/FieldSchemaEditor/Inserter';
import { Column } from 'dashboard/components-lib/FieldSchemaEditor/table';
import { StyledIconDrag } from 'dashboard/lib/dnd/StyledIconDrag';
import { Identifier } from 'dashboard/models/identifier';
import Spacer from 'design-system/components/Spacer';
import { TableBody, TableCell, TableRow } from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { isFocused } from 'design-system/lib/pseudoClass';
import { TransitionDuration } from 'design-system/variables/transitions';
import { FieldSchema, FieldSchemaArray, FieldSchemaHash, FieldSchemaType } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';

const DragIconContainer = styled.span`
  position: relative;
  display: inline-block;
  width: 5px;
  height: ${Spacing[4]};
  left: -${Spacing[2]};
  top: ${Spacing[1]};
`;

const StyledDragIcon = styled(StyledIconDrag)<{ $visible: boolean; $isDragging: boolean }>`
  width: ${Spacing[4]};
  height: ${Spacing[4]};
  cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'grab')};
  visibility: ${({ $visible = false }) => ($visible ? 'visible' : 'hidden')};
`;

interface DragHandleProps {
  dragHandleProps: DraggableProvidedDragHandleProps;
  disableDrag: boolean;
  isDragging: boolean;
  dataTest: string;
}

const DragHandle: React.FC<DragHandleProps> = ({
  dragHandleProps,
  disableDrag,
  isDragging = false,
  dataTest = '',
}) => {
  const [isMouseOver, { enable: handleMouseEnter, disable: handleMouseLeave }] = useToggle(false);
  return (
    <DragIconContainer
      {...dragHandleProps}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-test={dataTest}
    >
      <StyledDragIcon
        $active={isMouseOver || isDragging}
        $shouldHide={disableDrag}
        $visible={isMouseOver || isDragging}
        $isDragging={isDragging}
      />
    </DragIconContainer>
  );
};

const HighlightableTableRow = styled(TableRow)<{ hasEmphasis: boolean; hasError: boolean }>`
  cursor: pointer;
  background-color: ${({ hasEmphasis, theme }) =>
    hasEmphasis ? theme.color.background.highlight : 'transparent'};
  ${({ hasError, theme }) =>
    hasError ? `background-color: ${theme.color.variants.danger.highlight}` : ''};

  &${isFocused} {
    z-index: 1;
    position: relative;
    box-shadow: inset 0 0 0 3px ${({ theme }) => theme.color.variants.primary.highlight};
  }
`;

const HighlightableTableCell = styled(TableCell)<{ highlight: boolean; noBorder: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ highlight, theme }) => (highlight ? '' : `color: ${theme.color.text.secondary}`)};
  ${({ noBorder }) => (noBorder ? 'border: 0' : '')}
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledChevronIcon = styled(ChevronRightIcon)<{ expanded: number }>`
  transform: rotate(${({ expanded }) => (expanded ? '90deg' : '0')});
  transition: transform ${TransitionDuration.Fast};
  margin-right: ${Spacing[1]};
`;

const StyledTooltipContainer = styled.div`
  display: inline-flex;
  color: ${Color.CoreYellow800};
  margin-left: ${Spacing[1]};
`;

const DroppablePlaceHolder = styled(TableCell)`
  padding: 0;
  border: 0;
`;

const PlaceholderText = styled.span`
  color: ${({ theme }) => theme.color.text.tertiary};
  font-style: italic;
  margin-right: ${Spacing[1]};
`;

const extractSubrowsFromArray = (schema: FieldSchemaArray): FieldSchema[] => {
  const subSchema = schema.config['item-schema'];
  return subSchema?.type === FieldSchemaType.Hash ? subSchema?.config['item-schemas'] : [];
};

const isExpandable = (schema: EditorableSchema) => {
  return (
    schema.type === FieldSchemaType.Hash ||
    (schema.type === FieldSchemaType.Array &&
      schema.config['item-schema']?.type === FieldSchemaType.Hash)
  );
};

const getKeyPath = (prefix: string, key: string, index: number) => `${prefix}.${key || index}`;

interface FieldRowProps {
  prefix: string;
  inputSchema: EditorableSchema;
  customColumns?: Column[];
  focusedKey?: string;
  erroredKeys?: string[];
  uneditableKeys?: string[];
  preexistingKeys?: string[];
  nestLevel?: number;
  hasEditPermission?: boolean;
  isDragging?: boolean;
  isLastRow?: boolean;
  onInsertSibling?: (siblingSchema: EditorableSchema, identifierConfig?: Identifier) => void;
  onUpdateFieldSchema: (fieldSchema: EditorableSchema) => void;
  onFocusFieldSchema: (path: string, focusedReason: FocusReason) => void;
}

/**
 * FieldRow is an abstraction of TableBody that contains one field schema.
 * The TableBody could contain up to 4 TableRows:
 * - a TableRow that contains information about the field schema
 * - (optional) a TableRow that can be toggled to show the children of the field schema
 *    - the toggleable TableRow may contain a nested Table
 * - (optional) a TableRow that is the highlighted border to insert a new field schema right after this one
 * - a TableRow that is a Droppable placeholder
 */
const FieldRow: React.FC<FieldRowProps> = ({
  prefix,
  inputSchema,
  customColumns = [],
  erroredKeys = [],
  focusedKey = '',
  uneditableKeys = [],
  // In most cases, already saved (preexisting) keys are the same as the uneditable keys since preexisting
  // keys should not be changed. However, for account system keys, preexisting keys and uneditable keys are
  // not the same set of keys (system fields are uneditable but may not be preexisting), and so we have to
  // separate them out.
  preexistingKeys = uneditableKeys,
  nestLevel = 0,
  hasEditPermission = true,
  isDragging = false,
  isLastRow = false,
  onInsertSibling,
  onUpdateFieldSchema,
  onFocusFieldSchema,
}) => {
  const [expanded, { enable: expandSubRows, toggle }] = useToggle();
  const expandable = useMemo(() => isExpandable(inputSchema), [inputSchema]);
  const editable = useMemo(() => {
    return hasEditPermission && !uneditableKeys.includes(prefix);
  }, [prefix, uneditableKeys, hasEditPermission]);
  const highlighted = useMemo(() => {
    return hasEditPermission || focusedKey === prefix;
  }, [hasEditPermission, focusedKey, prefix]);
  const hasError = useMemo(() => {
    return erroredKeys.includes(prefix);
  }, [erroredKeys, prefix]);
  const subRows = useMemo(() => {
    const sharedProps: Omit<
      FieldRowProps,
      'prefix' | 'inputSchema' | 'isLastRow' | 'onInsertSibling' | 'onUpdateFieldSchema'
    > = {
      customColumns,
      focusedKey,
      erroredKeys,
      uneditableKeys,
      preexistingKeys,
      nestLevel: nestLevel + 1,
      hasEditPermission,
      isDragging,
      onFocusFieldSchema,
    };
    switch (inputSchema.type) {
      case FieldSchemaType.Hash:
        return inputSchema.config['item-schemas']
          ?.filter((schema) => !schema?.config?.['deactivated-at'])
          .map((field, subSchemaIndex, subSchemas) => {
            const key = getKeyPath(prefix, field.key, subSchemaIndex);
            return (
              <FieldRow
                key={key}
                {...sharedProps}
                prefix={key}
                inputSchema={field}
                isLastRow={subSchemaIndex === subSchemas.length - 1}
                onInsertSibling={(sibling: EditorableSchema) => {
                  if (sibling.type !== RelationSchemaType.RelationSchema) {
                    const siblingIndex = subSchemaIndex + 1;
                    const updatedSchema = produce(inputSchema, (draftSchema) => {
                      draftSchema.config['item-schemas'].splice(siblingIndex, 0, sibling);
                    });
                    onUpdateFieldSchema(updatedSchema);
                    onFocusFieldSchema(getKeyPath(prefix, sibling.key, siblingIndex), 'add');
                  }
                }}
                onUpdateFieldSchema={(updatedSelf: EditorableSchema) => {
                  if (updatedSelf.type !== RelationSchemaType.RelationSchema) {
                    const updatedSchema = produce(inputSchema, (draftSchema) => {
                      draftSchema.config['item-schemas'].splice(subSchemaIndex, 1, updatedSelf);
                    });
                    onUpdateFieldSchema(updatedSchema);
                  }
                }}
              />
            );
          });
      case FieldSchemaType.Array:
        return extractSubrowsFromArray(inputSchema)
          .filter((schema) => !schema?.config?.['deactivated-at'])
          .map((field, subSchemaIndex, subSchemas) => {
            const key = getKeyPath(`${prefix}.[]`, field.key, subSchemaIndex);
            return (
              <FieldRow
                key={key}
                {...sharedProps}
                prefix={key}
                inputSchema={field}
                isLastRow={subSchemaIndex === subSchemas.length - 1}
                onInsertSibling={(sibling: EditorableSchema) => {
                  if (sibling.type !== RelationSchemaType.RelationSchema) {
                    const siblingIndex = subSchemaIndex + 1;
                    const updatedSchema = produce(inputSchema, (draftSchema) => {
                      (draftSchema.config['item-schema'] as FieldSchemaHash).config[
                        'item-schemas'
                      ].splice(siblingIndex, 0, sibling);
                    });
                    onUpdateFieldSchema(updatedSchema);
                    onFocusFieldSchema(
                      getKeyPath(`${prefix}.[]`, sibling.key, siblingIndex),
                      'add',
                    );
                  }
                }}
                onUpdateFieldSchema={(updatedSelf: EditorableSchema) => {
                  if (updatedSelf.type !== RelationSchemaType.RelationSchema) {
                    const updatedSchema = produce(inputSchema, (draftSchema) => {
                      (draftSchema.config['item-schema'] as FieldSchemaHash).config[
                        'item-schemas'
                      ].splice(subSchemaIndex, 1, updatedSelf);
                    });
                    onUpdateFieldSchema(updatedSchema);
                  }
                }}
              />
            );
          });
      default:
    }
  }, [
    customColumns,
    erroredKeys,
    focusedKey,
    inputSchema,
    nestLevel,
    prefix,
    uneditableKeys,
    preexistingKeys,
    hasEditPermission,
    isDragging,
    onFocusFieldSchema,
    onUpdateFieldSchema,
  ]);

  const onFocus = () => {
    if (expandable) {
      toggle();
    }
    onFocusFieldSchema(prefix, 'edit');
  };

  useEffect(() => {
    function isDescendant(target: string) {
      return target?.startsWith(`${prefix}.`) || target === prefix;
    }
    if (expandable && (erroredKeys.some(isDescendant) || isDescendant(focusedKey))) {
      expandSubRows();
    }
  }, [erroredKeys, expandSubRows, expandable, prefix, focusedKey]);

  return (
    <Droppable
      droppableId={convertDroppableId('', prefix)}
      isDropDisabled={(expandable && expanded) || !hasEditPermission}
    >
      {(droppableProvided) => {
        return (
          <TableBody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
            <Draggable
              draggableId={convertDraggableId('', prefix)}
              index={0}
              isDragDisabled={(expandable && expanded) || !editable}
            >
              {(draggableProvided, snapshot) => {
                return (
                  <HighlightableTableRow
                    tabIndex={0}
                    ref={draggableProvided.innerRef}
                    data-cy={`fields-${inputSchema.key}__table-row`}
                    hasError={hasError}
                    hasEmphasis={focusedKey === prefix || snapshot.isDragging}
                    id={`${prefix}-expand`}
                    aria-label={`Expand ${prefix} field`}
                    onKeyDown={(evt) => {
                      if (evt.key === ' ' || evt.key === 'Enter') {
                        evt.preventDefault();
                        evt.stopPropagation();
                        onFocus();
                      }
                    }}
                    onClick={onFocus}
                    {...draggableProvided.draggableProps}
                  >
                    <HighlightableTableCell
                      highlight={highlighted}
                      noBorder={(expandable && expanded) || isLastRow || snapshot.isDragging}
                    >
                      <FlexContainer>
                        <Spacer width={4 * nestLevel} height={0} />
                        {expandable && (
                          <div aria-label={expanded ? 'Hide nested fields' : 'Show nested fields'}>
                            <StyledChevronIcon expanded={expanded ? 1 : 0} />
                          </div>
                        )}
                        <DragHandle
                          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          dragHandleProps={draggableProvided.dragHandleProps}
                          disableDrag={(expandable && expanded) || !editable}
                          isDragging={snapshot.isDragging}
                          dataTest={`${prefix}-drag-handle`}
                        />
                        <span>
                          {inputSchema.key ? (
                            inputSchema.key
                          ) : (
                            <PlaceholderText>Unnamed Field</PlaceholderText>
                          )}
                        </span>
                        {!preexistingKeys.includes(prefix) && (
                          <StyledTooltipContainer>
                            <Tooltip side='top' iconTrigger={<EditIcon size={11} />}>
                              <Paragraph color='primary' size='sm'>
                                Unsaved changes
                              </Paragraph>
                            </Tooltip>
                          </StyledTooltipContainer>
                        )}
                      </FlexContainer>
                    </HighlightableTableCell>
                    {customColumns.map((column, columnIndex) => {
                      return (
                        <HighlightableTableCell
                          key={`${prefix}-${columnIndex}-${column.header}`}
                          highlight={highlighted}
                          noBorder={(expandable && expanded) || isLastRow || snapshot.isDragging}
                          style={{ width: column.width ?? 'auto' }}
                        >
                          <FlexContainer>
                            {column.generateContent({
                              field: inputSchema,
                              prefix,
                              canBeDeleted: editable && (nestLevel === 0 || !isLastRow),
                              highlighted: highlighted,
                              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                              insertSibling: onInsertSibling,
                              updateSelf: onUpdateFieldSchema,
                              setFocusedSchema: onFocusFieldSchema,
                            })}
                          </FlexContainer>
                        </HighlightableTableCell>
                      );
                    })}
                  </HighlightableTableRow>
                );
              }}
            </Draggable>
            {expandable && (
              <ExpandableFieldRow
                colSpan={customColumns.length + 1}
                nestLevel={nestLevel}
                showBottomBorder={!isLastRow}
                visible={expanded}
              >
                {subRows}
              </ExpandableFieldRow>
            )}
            {hasEditPermission && onInsertSibling && !expanded && !isDragging && (
              <Inserter
                prefix={prefix}
                colSpan={customColumns.length + 1}
                nestSpacing={NEST_SPACING * nestLevel}
                onAddRow={onInsertSibling}
              />
            )}
            <TableRow>
              <DroppablePlaceHolder colSpan={customColumns.length + 1}>
                {droppableProvided.placeholder}
              </DroppablePlaceHolder>
            </TableRow>
          </TableBody>
        );
      }}
    </Droppable>
  );
};

export default FieldRow;
